import { useMemo, useState, useCallback } from "react";
import { useSelector } from '@xstate/store/react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Pencil, GripVertical } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import type { Employee, Task, GlobalRequirement, ColumnDefinition, TaskCategory } from "../types";
import { globalTasksStore } from "../stores/globalTasksStore";
import { pertExpectedValue } from "../types";
import { useAllTasks, useScheduledTasks, useBoardStartDate, useTaskCategories } from "../stores";
import { getTaskDayIndex, getKwFromDate, formatDateDE, businessDayIndexToDate } from "../lib/dateUtils";
import { BlockNoteReadOnly } from "./BlockNoteReadOnly";
import { getMonthNamesShort } from '../boardv3/utils/boardHelpers';

type ViewMode = "columns" | "timeline";

interface OverviewProps {
  columns: string[];
  columnDefs?: ColumnDefinition[];
  employees: Employee[];
  globalRequirements?: GlobalRequirement[];
  onAddGlobalRequirement?: (text: string, type: 'hard' | 'soft', description: string) => void;
  onUpdateGlobalRequirement?: (text: string, updates: Partial<GlobalRequirement>) => void;
  onDeleteGlobalRequirement?: (text: string) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  onAddTask?: (column?: string) => void;
  onTaskClick?: (taskId: string) => void;
  onMoveTask?: (taskId: string, newColumn: string) => void;
}

export function Overview({
  columns,
  columnDefs = [],
  employees,
  globalRequirements = [],
  onAddGlobalRequirement,
  onUpdateGlobalRequirement,
  onDeleteGlobalRequirement,
  viewMode: controlledViewMode,
  onViewModeChange,
  onAddTask,
  onTaskClick,
  onMoveTask,
}: OverviewProps) {
  const { t, i18n } = useTranslation();
  const boardStartDate = useBoardStartDate();
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>("columns");

  // Modal state for add/edit global requirement
  const [reqModalOpen, setReqModalOpen] = useState(false);
  const [reqModalEdit, setReqModalEdit] = useState<GlobalRequirement | null>(null); // null = add mode
  const [reqModalText, setReqModalText] = useState('');
  const [reqModalType, setReqModalType] = useState<'hard' | 'soft'>('hard');
  const [reqModalDescription, setReqModalDescription] = useState('');

  // Set of global requirement texts for quick lookup
  const globalReqTexts = useMemo(() => new Set(globalRequirements.map(r => r.text)), [globalRequirements]);

  const openAddModal = useCallback((type: 'hard' | 'soft') => {
    setReqModalEdit(null);
    setReqModalText('');
    setReqModalType(type);
    setReqModalDescription('');
    setReqModalOpen(true);
  }, []);

  const openEditModal = useCallback((req: GlobalRequirement) => {
    setReqModalEdit(req);
    setReqModalText(req.text);
    setReqModalType(req.type);
    setReqModalDescription(req.description);
    setReqModalOpen(true);
  }, []);

  const handleReqModalSubmit = useCallback(() => {
    if (!reqModalText.trim() || !reqModalDescription.trim()) return;
    if (reqModalEdit) {
      onUpdateGlobalRequirement?.(reqModalEdit.text, {
        text: reqModalText.trim(),
        type: reqModalType,
        description: reqModalDescription.trim(),
      });
    } else {
      onAddGlobalRequirement?.(reqModalText.trim(), reqModalType, reqModalDescription.trim());
    }
    setReqModalOpen(false);
  }, [reqModalEdit, reqModalText, reqModalType, reqModalDescription, onAddGlobalRequirement, onUpdateGlobalRequirement]);

  // Use controlled mode if props are provided, otherwise use internal state
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = onViewModeChange ?? setInternalViewMode;
  const tasks = useAllTasks();
  const scheduledTasks = useScheduledTasks();

  // Unprioritized tasks (global tasks not on this board)
  const [showUnprioritized, setShowUnprioritized] = useState(false);
  const globalTaskIds = useSelector(globalTasksStore, (s) => s.context.taskIds);
  const unprioritizedTasks = useMemo(() => {
    const { tasksById } = globalTasksStore.getSnapshot().context;
    const boardTaskIds = new Set(tasks.map(t => t.id));
    return globalTaskIds
      .filter(id => !boardTaskIds.has(id))
      .map(id => {
        const gt = tasksById[id];
        return gt ? { ...gt, column: '' } as Task : null;
      })
      .filter((t): t is Task => t !== null);
  }, [globalTaskIds, tasks]);

  // Category lookup for display
  const categories = useTaskCategories();
  const categoryMap = useMemo(() => {
    const map = new Map<string, TaskCategory>();
    for (const cat of categories) map.set(cat.id, cat);
    return map;
  }, [categories]);

  // Per-column unprioritized tasks (matched by category)
  const unprioritizedByColumn = useMemo(() => {
    if (!showUnprioritized || unprioritizedTasks.length === 0) return new Map<string, Task[]>();

    const result = new Map<string, Task[]>();
    const assigned = new Set<string>();

    for (const colDef of columnDefs) {
      const catSet = new Set(colDef.defaultCategoryIds);
      if (catSet.size === 0) {
        result.set(colDef.name, []);
        continue;
      }
      const matching = unprioritizedTasks.filter(t =>
        t.categoryIds.some(catId => catSet.has(catId))
      );
      result.set(colDef.name, matching);
      for (const t of matching) assigned.add(t.id);
    }

    // Remaining unmatched tasks
    const remaining = unprioritizedTasks.filter(t => !assigned.has(t.id));
    if (remaining.length > 0) {
      result.set('__unmatched__', remaining);
    }

    return result;
  }, [showUnprioritized, columnDefs, unprioritizedTasks]);

  // Create employee lookup
  const employeeMap = useMemo(() => {
    const map = new Map<string, Employee>();
    for (const emp of employees) {
      map.set(emp.id, emp);
    }
    return map;
  }, [employees]);

  // Group tasks by column
  const tasksByColumn = useMemo(() => {
    const grouped = new Map<string, Task[]>();

    for (const col of columns) {
      grouped.set(col, []);
    }

    for (const task of tasks) {
      const group = grouped.get(task.column);
      if (group) {
        group.push(task);
      }
    }

    // Sort tasks by startDate
    for (const group of grouped.values()) {
      group.sort((a, b) => a.startDate.localeCompare(b.startDate));
    }

    return grouped;
  }, [tasks, columns]);

  // Group tasks by week for timeline view
  const tasksByWeek = useMemo(() => {
    const sorted = [...tasks].sort((a, b) => a.startDate.localeCompare(b.startDate));

    const grouped = new Map<number, Task[]>();

    for (const task of sorted) {
      const week = getKwFromDate(task.startDate).kw;
      if (!grouped.has(week)) {
        grouped.set(week, []);
      }
      grouped.get(week)!.push(task);
    }

    // Convert to sorted array
    return Array.from(grouped.entries()).sort((a, b) => a[0] - b[0]);
  }, [tasks]);

  // Calculate stats
  const stats = useMemo(() => {
    let totalTasks = 0;
    let totalWeeks = 0;

    for (const group of tasksByColumn.values()) {
      totalTasks += group.length;

      for (const task of group) {
        const scheduled = scheduledTasks.get(task.id);
        if (scheduled?.teamWeeks) {
          totalWeeks += scheduled.teamWeeks;
        }
      }
    }

    return { totalTasks, totalWeeks: Math.round(totalWeeks) };
  }, [tasksByColumn, scheduledTasks]);

  // Aggregate requirements from all active tasks
  interface AggregatedRequirement {
    text: string;
    earliestStartDate: string | null; // null = global requirement without task
    globalDescription?: string; // Description from global requirement
    tasks: { id: string; title: string; startDate: string; description?: string }[];
  }

  const aggregatedRequirements = useMemo(() => {
    const hardMap = new Map<string, AggregatedRequirement>();
    const softMap = new Map<string, AggregatedRequirement>();

    // Collect all tasks
    const allTasks: Task[] = [];
    for (const group of tasksByColumn.values()) {
      allTasks.push(...group);
    }

    for (const task of allTasks) {
      // Process hard requirements
      if (task.requirements_hard) {
        for (const req of task.requirements_hard) {
          const existing = hardMap.get(req.text);
          if (existing) {
            existing.tasks.push({ id: task.id, title: task.title, startDate: task.startDate, description: req.description });
            if (existing.earliestStartDate === null || task.startDate < existing.earliestStartDate) {
              existing.earliestStartDate = task.startDate;
            }
          } else {
            hardMap.set(req.text, {
              text: req.text,
              earliestStartDate: task.startDate,
              tasks: [{ id: task.id, title: task.title, startDate: task.startDate, description: req.description }],
            });
          }
        }
      }

      // Process soft requirements
      if (task.requirements_soft) {
        for (const req of task.requirements_soft) {
          const existing = softMap.get(req.text);
          if (existing) {
            existing.tasks.push({ id: task.id, title: task.title, startDate: task.startDate, description: req.description });
            if (existing.earliestStartDate === null || task.startDate < existing.earliestStartDate) {
              existing.earliestStartDate = task.startDate;
            }
          } else {
            softMap.set(req.text, {
              text: req.text,
              earliestStartDate: task.startDate,
              tasks: [{ id: task.id, title: task.title, startDate: task.startDate, description: req.description }],
            });
          }
        }
      }
    }

    // Process global requirements
    for (const globalReq of globalRequirements) {
      const targetMap = globalReq.type === 'hard' ? hardMap : softMap;
      const existing = targetMap.get(globalReq.text);
      if (existing) {
        // Merge: add global description
        existing.globalDescription = globalReq.description;
      } else {
        // Create new entry for global-only requirement
        targetMap.set(globalReq.text, {
          text: globalReq.text,
          earliestStartDate: null,
          globalDescription: globalReq.description,
          tasks: [],
        });
      }
    }

    // Sort by earliest deadline (null = no specific deadline, sort to end)
    const sortByDeadline = (a: AggregatedRequirement, b: AggregatedRequirement) => {
      if (a.earliestStartDate === null && b.earliestStartDate === null) return 0;
      if (a.earliestStartDate === null) return 1;
      if (b.earliestStartDate === null) return -1;
      return a.earliestStartDate.localeCompare(b.earliestStartDate);
    };
    const hard = Array.from(hardMap.values()).sort(sortByDeadline);
    const soft = Array.from(softMap.values()).sort(sortByDeadline);

    return { hard, soft };
  }, [tasksByColumn, globalRequirements]);

  // Get KW display for a date string
  const getKWForDate = (dateStr: string): string => {
    const { kw } = getKwFromDate(dateStr);
    return t('overview.calendarWeek', { week: kw });
  };

  // Get assignee display as elements with colored dots
  const getAssigneeElements = (task: Task) => {
    if (task.assigneeIds.length === 0) return <span className="text-gray-400">—</span>;
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {task.assigneeIds.map((id) => {
          const emp = employeeMap.get(id);
          return (
            <span key={id} className="flex items-center gap-1">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: emp?.color || '#9ca3af' }}
              />
              <span className="text-[10px] text-gray-600">{emp?.name || id}</span>
            </span>
          );
        })}
      </div>
    );
  };

  // Get duration display
  const getDurationDisplay = (task: Task): string => {
    const scheduled = scheduledTasks.get(task.id);
    if (scheduled?.teamWeeks) {
      return `${scheduled.teamWeeks.toFixed(1)}w`;
    }

    // Fallback to estimate from primary assignee
    if (task.assigneeIds.length > 0) {
      const primaryId = task.assigneeIds[0];
      const estimate = task.estimates[primaryId];
      if (estimate) {
        return `~${pertExpectedValue(estimate).toFixed(1)}w`;
      }
    }

    return "—";
  };

  // Get start week display
  const getStartWeekDisplay = (task: Task): string => {
    const { kw } = getKwFromDate(task.startDate);
    return `KW ${kw}`;
  };

  // Get end week display
  const getEndWeekDisplay = (task: Task): string => {
    const scheduled = scheduledTasks.get(task.id);
    const durationDays = scheduled?.effectiveDays || 10;
    const endDayIdx = getTaskDayIndex(task.startDate, boardStartDate) + durationDays;
    const endDate = businessDayIndexToDate(endDayIdx, boardStartDate);
    const { kw } = getKwFromDate(endDate);
    return `KW ${kw}`;
  };

  // DnD state
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const task = event.active.data.current?.task as Task | undefined;
    if (task) setDraggedTask(task);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setDraggedTask(null);
    const { active, over } = event;
    if (!over || !onMoveTask) return;
    const task = active.data.current?.task as Task | undefined;
    if (!task) return;
    const targetColumn = over.data.current?.column as string | undefined;
    if (targetColumn && targetColumn !== task.column) {
      onMoveTask(task.id, targetColumn);
    }
  }, [onMoveTask]);

  // Get month name for a KW
  const getMonthForKW = (kw: number): string => {
    // Approximate: KW 1-4 = Jan, KW 5-8 = Feb, etc.
    const months = getMonthNamesShort(i18n.language);
    const monthIndex = Math.floor((kw - 1) / 4.33);
    return months[Math.min(monthIndex, 11)];
  };

  // Render a single task row (shared between column and timeline views)
  const renderTaskRow = (task: Task, extras?: { showColumn?: boolean; showEndWeek?: boolean }) => (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-16">
        <span className="text-[10px] font-mono text-gray-400">{task.id}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-medium text-gray-900">{task.title}</p>
          {task.categoryIds.length > 0 && (
            <div className="flex items-center gap-0.5">
              {task.categoryIds.map(catId => {
                const cat = categoryMap.get(catId);
                if (!cat) return null;
                return (
                  <span
                    key={catId}
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: cat.color }}
                    title={cat.name}
                  />
                );
              })}
            </div>
          )}
        </div>
        {extras?.showColumn && (
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
              {task.column}
            </span>
          </div>
        )}
        {Array.isArray(task.description) && task.description.length > 0 && (
          <div className="text-[10px] text-gray-500 mt-0.5">
            <span className="font-medium text-gray-500">{t('task.description') + ': '}</span>
            <BlockNoteReadOnly content={task.description} />
          </div>
        )}
        {Array.isArray(task.consequence) && task.consequence.length > 0 && (
          <div className="text-[10px] text-red-600 mt-0.5">
            <span className="font-medium">{t('task.consequence') + ': '}</span>
            <BlockNoteReadOnly content={task.consequence} />
          </div>
        )}
        {(task.requirements_hard?.length || task.requirements_soft?.length) ? (
          <div className="mt-1 p-1.5 bg-gray-50 border border-gray-100 rounded text-[10px] space-y-1">
            {task.requirements_hard && task.requirements_hard.length > 0 && (
              <div>
                <div className="font-medium text-gray-500 mb-0.5">{t('overview.hardLabel')}</div>
                {task.requirements_hard.map((req) => (
                  <div key={req.text} className="ml-2 mb-0.5">
                    <a
                      href={`#req-hard-${req.text.replace(/[^a-zA-Z0-9]/g, '-')}`}
                      className="text-gray-600 hover:text-gray-800 hover:underline font-medium"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        document.getElementById(`req-hard-${req.text.replace(/[^a-zA-Z0-9]/g, '-')}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                    >
                      {req.text}
                    </a>
                    {req.description && <span className="text-gray-400 ml-1">— {req.description}</span>}
                  </div>
                ))}
              </div>
            )}
            {task.requirements_soft && task.requirements_soft.length > 0 && (
              <div>
                <div className="font-medium text-gray-500 mb-0.5">{t('overview.softLabel')}</div>
                {task.requirements_soft.map((req) => (
                  <div key={req.text} className="ml-2 mb-0.5">
                    <a
                      href={`#req-soft-${req.text.replace(/[^a-zA-Z0-9]/g, '-')}`}
                      className="text-gray-600 hover:text-gray-800 hover:underline font-medium"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        document.getElementById(`req-soft-${req.text.replace(/[^a-zA-Z0-9]/g, '-')}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                    >
                      {req.text}
                    </a>
                    {req.description && <span className="text-gray-400 ml-1">— {req.description}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
      <div className="flex-shrink-0 flex items-center gap-3 text-[10px]">
        {extras?.showEndWeek ? (
          <span className="text-gray-400 whitespace-nowrap">
            {getStartWeekDisplay(task)} → {getEndWeekDisplay(task)}
          </span>
        ) : (
          <span className="text-gray-400">{getStartWeekDisplay(task)}</span>
        )}
        <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
          {getDurationDisplay(task)}
        </span>
        {getAssigneeElements(task)}
      </div>
    </div>
  );

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-gray-900">{t('overview.title')}</h1>
            <span className="text-xs text-gray-400">{t('overview.stats', { taskCount: stats.totalTasks, weekCount: stats.totalWeeks })}</span>
            {onAddTask && (
              <button
                onClick={() => onAddTask()}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-0.5 transition-colors"
              >
                <Plus size={12} />
                {t('nav.tasks')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unprioritizedTasks.length > 0 && (
              <button
                onClick={() => setShowUnprioritized(v => !v)}
                className={`px-2 py-0.5 rounded text-xs font-medium transition-all ${
                  showUnprioritized
                    ? "bg-amber-100 text-amber-700 border border-amber-300"
                    : "text-gray-400 hover:text-gray-600 border border-transparent"
                }`}
              >
                {t('overview.unprioritizedCount', { count: unprioritizedTasks.length })}
              </button>
            )}
            <div className="flex gap-0.5 bg-gray-100 rounded p-0.5">
              <button
                onClick={() => setViewMode("columns")}
                className={`px-2 py-0.5 rounded text-xs font-medium transition-all ${
                  viewMode === "columns"
                    ? "bg-gray-900 text-white"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t('overview.columnsView')}
              </button>
              <button
                onClick={() => setViewMode("timeline")}
                className={`px-2 py-0.5 rounded text-xs font-medium transition-all ${
                  viewMode === "timeline"
                    ? "bg-gray-900 text-white"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t('overview.timelineView')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Aggregated Requirements Section */}
      <div className="mb-3 space-y-2">
        {/* Hard Requirements */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
            <h3 className="text-xs font-medium text-gray-700 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
              {t('overview.hardRequirements')} ({aggregatedRequirements.hard.length})
              <span className="font-normal text-gray-400">— {t('overview.hardRequirementsDesc')}</span>
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {aggregatedRequirements.hard.map((req) => (
              <div key={req.text} id={`req-hard-${req.text.replace(/[^a-zA-Z0-9]/g, '-')}`} className="px-3 py-1.5 scroll-mt-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-gray-900">{req.text}</span>
                      <span className="text-[10px] text-gray-400">
                        {req.earliestStartDate !== null ? (
                          <>{t('overview.byDate', { kw: getKWForDate(req.earliestStartDate), date: formatDateDE(req.earliestStartDate) })}</>
                        ) : (
                          t('common.general')
                        )}
                      </span>
                    </div>
                    {req.globalDescription && (
                      <p className="text-[10px] text-gray-500 italic">{req.globalDescription}</p>
                    )}
                    {req.tasks.length > 0 && (
                      <div className="mt-0.5 space-y-0.5">
                        {req.tasks.map((task) => (
                          <div key={task.id} className="flex items-start gap-1.5 text-[10px]">
                            <a
                              href={`#task-${task.id}`}
                              className="font-mono text-gray-500 hover:text-gray-700 hover:underline flex-shrink-0"
                              onClick={(e) => {
                                e.preventDefault();
                                document.getElementById(`task-${task.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }}
                            >
                              {task.id}
                            </a>
                            {Array.isArray(task.description) && task.description.length > 0 ? (
                              <span className="text-gray-600"><BlockNoteReadOnly content={task.description} /></span>
                            ) : (
                              <span className="text-gray-400 italic">{task.title}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {globalReqTexts.has(req.text) && (
                    <div className="flex-shrink-0 flex items-center gap-1">
                      {onUpdateGlobalRequirement && (
                        <button
                          onClick={() => openEditModal(globalRequirements.find(g => g.text === req.text)!)}
                          className="p-1 text-gray-300 hover:text-gray-500 rounded hover:bg-gray-100 transition-colors"
                          title={t('common.edit')}
                        >
                          <Pencil size={12} />
                        </button>
                      )}
                      {onDeleteGlobalRequirement && (
                        <button
                          onClick={() => onDeleteGlobalRequirement(req.text)}
                          className="p-1 text-gray-300 hover:text-gray-500 rounded hover:bg-gray-100 transition-colors"
                          title={t('common.delete')}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {aggregatedRequirements.hard.length === 0 && (
              <div className="px-3 py-1.5 text-[10px] text-gray-400 italic">{t('overview.noHardRequirements')}</div>
            )}
            {onAddGlobalRequirement && (
              <button
                onClick={() => openAddModal('hard')}
                className="w-full px-3 py-1 text-left text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 flex items-center gap-1 transition-colors"
              >
                <Plus size={10} />
                {t('overview.addHardRequirement')}
              </button>
            )}
          </div>
        </div>

        {/* Soft Requirements */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
            <h3 className="text-xs font-medium text-gray-700 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
              {t('overview.softRequirements')} ({aggregatedRequirements.soft.length})
              <span className="font-normal text-gray-400">— {t('overview.softRequirementsDesc')}</span>
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {aggregatedRequirements.soft.map((req) => (
              <div key={req.text} id={`req-soft-${req.text.replace(/[^a-zA-Z0-9]/g, '-')}`} className="px-3 py-1.5 scroll-mt-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-gray-900">{req.text}</span>
                      <span className="text-[10px] text-gray-400">
                        {req.earliestStartDate !== null ? (
                          <>{t('overview.byDate', { kw: getKWForDate(req.earliestStartDate), date: formatDateDE(req.earliestStartDate) })}</>
                        ) : (
                          t('common.general')
                        )}
                      </span>
                    </div>
                    {req.globalDescription && (
                      <p className="text-[10px] text-gray-500 italic">{req.globalDescription}</p>
                    )}
                    {req.tasks.length > 0 && (
                      <div className="mt-0.5 space-y-0.5">
                        {req.tasks.map((task) => (
                          <div key={task.id} className="flex items-start gap-1.5 text-[10px]">
                            <a
                              href={`#task-${task.id}`}
                              className="font-mono text-gray-500 hover:text-gray-700 hover:underline flex-shrink-0"
                              onClick={(e) => {
                                e.preventDefault();
                                document.getElementById(`task-${task.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }}
                            >
                              {task.id}
                            </a>
                            {Array.isArray(task.description) && task.description.length > 0 ? (
                              <span className="text-gray-600"><BlockNoteReadOnly content={task.description} /></span>
                            ) : (
                              <span className="text-gray-400 italic">{task.title}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {globalReqTexts.has(req.text) && (
                    <div className="flex-shrink-0 flex items-center gap-1">
                      {onUpdateGlobalRequirement && (
                        <button
                          onClick={() => openEditModal(globalRequirements.find(g => g.text === req.text)!)}
                          className="p-1 text-gray-300 hover:text-gray-500 rounded hover:bg-gray-100 transition-colors"
                          title={t('common.edit')}
                        >
                          <Pencil size={12} />
                        </button>
                      )}
                      {onDeleteGlobalRequirement && (
                        <button
                          onClick={() => onDeleteGlobalRequirement(req.text)}
                          className="p-1 text-gray-300 hover:text-gray-500 rounded hover:bg-gray-100 transition-colors"
                          title={t('common.delete')}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {aggregatedRequirements.soft.length === 0 && (
              <div className="px-3 py-1.5 text-[10px] text-gray-400 italic">{t('overview.noSoftRequirements')}</div>
            )}
            {onAddGlobalRequirement && (
              <button
                onClick={() => openAddModal('soft')}
                className="w-full px-3 py-1 text-left text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 flex items-center gap-1 transition-colors"
              >
                <Plus size={10} />
                {t('overview.addSoftRequirement')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Global Requirement Modal */}
      {reqModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setReqModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-800">
                {reqModalEdit ? t('overview.requirementModal.editTitle') : t('overview.requirementModal.addTitle')}
              </h2>
              <button onClick={() => setReqModalOpen(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('common.name')}</label>
                <input
                  type="text"
                  value={reqModalText}
                  onChange={(e) => setReqModalText(e.target.value)}
                  placeholder={t('overview.requirementModal.namePlaceholder')}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('common.type')}</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setReqModalType('hard')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      reqModalType === 'hard'
                        ? 'bg-red-100 border-red-300 text-red-800'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t('overview.hardRequirements')}
                  </button>
                  <button
                    onClick={() => setReqModalType('soft')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      reqModalType === 'soft'
                        ? 'bg-yellow-100 border-yellow-300 text-yellow-800'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t('overview.softRequirements')}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('task.description')}</label>
                <textarea
                  value={reqModalDescription}
                  onChange={(e) => setReqModalDescription(e.target.value)}
                  placeholder={t('overview.requirementModal.descriptionPlaceholder')}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
              <button
                onClick={() => setReqModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReqModalSubmit}
                disabled={!reqModalText.trim() || !reqModalDescription.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {reqModalEdit ? t('common.save') : t('common.add')}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewMode === "columns" ? (
        // Column View with DnD
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="space-y-3">
            {columns.map((column) => {
              const columnTasks = tasksByColumn.get(column) ?? [];
              const columnUnprioritized = unprioritizedByColumn.get(column) ?? [];

              return (
                <DroppableColumn key={column} column={column} >
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    {/* Column Header */}
                    <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h2 className="text-sm font-medium text-gray-800">{column}</h2>
                          <span className="text-[10px] text-gray-400">{columnTasks.length}</span>
                        </div>
                        {onAddTask && (
                          <button
                            onClick={() => onAddTask(column)}
                            className="p-0.5 text-gray-300 hover:text-gray-500 hover:bg-gray-100 rounded transition-colors"
                            title={t('columns.addTaskToColumn', { column })}
                          >
                            <Plus size={14} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Tasks */}
                    <div className="divide-y divide-gray-100">
                      {columnTasks.length === 0 && columnUnprioritized.length === 0 && (
                        <div className="px-3 py-3 text-[10px] text-gray-400 italic">{t('columns.noTasks')}</div>
                      )}
                      {columnTasks.map((task) => (
                        <DraggableTaskRow
                          key={task.id}
                          task={task}
                          onTaskClick={onTaskClick}
                        >
                          {renderTaskRow(task)}
                        </DraggableTaskRow>
                      ))}
                      {/* Add Task Button */}
                      {onAddTask && (
                        <button
                          onClick={() => onAddTask(column)}
                          className="w-full px-3 py-1.5 text-left text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 flex items-center gap-1 transition-colors"
                        >
                          <Plus size={10} />
                          {t('task.addTask')}
                        </button>
                      )}
                    </div>

                    {/* Nicht einpriorisierte Tasks fuer diese Column */}
                    {columnUnprioritized.length > 0 && (
                      <div className="bg-amber-50/50 border-t border-amber-200">
                        <div className="px-3 py-1.5 border-b border-amber-100">
                          <span className="text-[10px] font-medium text-amber-700">
                            {t('overview.unprioritizedCount', { count: columnUnprioritized.length })}
                          </span>
                        </div>
                        <div className="divide-y divide-amber-100">
                          {columnUnprioritized.map((task) => (
                            <div
                              key={task.id}
                              className="px-3 py-1.5 hover:bg-amber-50 cursor-pointer"
                              onClick={() => onTaskClick?.(task.id)}
                            >
                              {renderTaskRow(task)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </DroppableColumn>
              );
            })}

            {/* Nicht zugeordnete Tasks (keine Kategorie-Übereinstimmung) */}
            {(unprioritizedByColumn.get('__unmatched__') ?? []).length > 0 && (
              <div className="bg-amber-50/50 border border-amber-200 rounded overflow-hidden">
                <div className="bg-amber-100/70 px-3 py-2 border-b border-amber-200">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xs font-medium text-amber-800">{t('overview.unprioritizedUnmatched')}</h2>
                    <span className="text-[10px] text-amber-500">{(unprioritizedByColumn.get('__unmatched__') ?? []).length}</span>
                  </div>
                </div>
                <div className="divide-y divide-amber-100">
                  {(unprioritizedByColumn.get('__unmatched__') ?? []).map((task) => (
                    <div
                      key={task.id}
                      className="px-3 py-1.5 hover:bg-amber-50 cursor-pointer"
                      onClick={() => onTaskClick?.(task.id)}
                    >
                      {renderTaskRow(task)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DragOverlay>
            {draggedTask && (
              <div className="bg-white border border-gray-300 rounded shadow-lg px-3 py-1.5 opacity-90 max-w-2xl">
                {renderTaskRow(draggedTask)}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        // Timeline View
        <div className="space-y-3">
          {tasksByWeek.map(([week, weekTasks]) => (
            <div key={week} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Week Header */}
              <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">{t('overview.calendarWeek', { week })}</span>
                    <span className="text-[10px] text-gray-400">({getMonthForKW(week)})</span>
                  </div>
                  <span className="text-[10px] text-gray-400">
                    {t('overview.taskCount', { count: weekTasks.length })}
                  </span>
                </div>
              </div>

              {/* Tasks */}
              <div className="divide-y divide-gray-100">
                {weekTasks.map((task) => (
                  <div
                    key={task.id}
                    id={`task-${task.id}`}
                    className="px-3 py-1.5 hover:bg-gray-50 scroll-mt-4 cursor-pointer"
                    onClick={() => onTaskClick?.(task.id)}
                  >
                    {renderTaskRow(task, { showColumn: true, showEndWeek: true })}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Nicht einpriorisierte Tasks */}
          {showUnprioritized && unprioritizedTasks.length > 0 && (
            <div className="bg-amber-50/50 border border-amber-200 rounded overflow-hidden">
              <div className="bg-amber-100/70 px-3 py-2 border-b border-amber-200">
                <div className="flex items-center gap-2">
                  <h2 className="text-xs font-medium text-amber-800">{t('overview.unprioritizedCount', { count: unprioritizedTasks.length })}</h2>
                  <span className="text-[10px] text-amber-500">{unprioritizedTasks.length}</span>
                </div>
              </div>
              <div className="divide-y divide-amber-100">
                {unprioritizedTasks.map((task) => (
                  <div
                    key={task.id}
                    className="px-3 py-1.5 hover:bg-amber-50 cursor-pointer"
                    onClick={() => onTaskClick?.(task.id)}
                  >
                    {renderTaskRow(task)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tasksByWeek.length === 0 && unprioritizedTasks.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-xs">
              {t('overview.noActiveTasks')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// DnD Helper Components
// ============================================

function DraggableTaskRow({
  task,
  onTaskClick,
  children,
}: {
  task: Task;
  onTaskClick?: (taskId: string) => void;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `task-${task.id}`,
    data: { type: 'task', task },
  });

  return (
    <div
      ref={setNodeRef}
      id={`task-${task.id}`}
      className={`px-3 py-1.5 hover:bg-gray-50 scroll-mt-4 cursor-pointer group flex items-center gap-1 ${
        isDragging ? 'opacity-30' : ''
      }`}
      onClick={() => onTaskClick?.(task.id)}
    >
      <div
        {...listeners}
        {...attributes}
        className="flex-shrink-0 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical size={12} />
      </div>
      <div className="flex-1 min-w-0">
        {children}
      </div>
    </div>
  );
}

function DroppableColumn({
  column,
  children,
}: {
  column: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver: isOverDroppable } = useDroppable({
    id: `column-${column}`,
    data: { type: 'column', column },
  });

  return (
    <div
      ref={setNodeRef}
      className={`transition-all ${isOverDroppable ? 'ring-2 ring-blue-400 ring-offset-1 rounded-lg' : ''}`}
    >
      {children}
    </div>
  );
}
